import { Navigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { PaywallGuard } from "@/components/billing/PaywallGuard";
import { AppSidebar } from "@/components/AppSidebar";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useRouteTracking } from "@/hooks/useRouteTracking";

const routeLabels: Record<string, string> = {
  // Operação
  "/dashboard": "Início",
  "/groups": "Grupos",
  "/members": "Membros",
  "/conversations": "Conversas",
  "/jarvis": "Jarvis",
  "/jarvis/new": "Nova sessão Jarvis",
  "/jarvis/history": "Histórico Jarvis",
  "/commands": "Comandos",
  "/copilot": "Copiloto",
  "/sales": "Vendas",
  "/support": "Suporte",
  // Cortex OS
  "/operacao": "Fluxo de decisão",
  "/operacao/avancada": "Cockpit Cortex",
  "/pipeline": "Pipeline (inspector)",
  "/realtime-flow": "Fluxo realtime",
  "/intents": "Intents",
  "/quality": "Qualidade",
  "/memoria/auditoria": "Auditoria de memória",
  "/memoria/quarentena": "Quarentena",
  "/governanca/forense": "Forense",
  "/governanca/regulacao": "Regulação",
  // Memória & conteúdo
  "/memory": "Memórias",
  "/knowledge-base": "Base de conhecimento",
  "/sources": "Fontes",
  "/prompt-library": "Biblioteca de prompts",
  // Governança
  "/sentinel": "Sentinel",
  "/security": "Segurança",
  "/intelligence": "Inteligência",
  // Aprendizado & modelos
  "/agents": "Personas",
  "/skills": "Skills",
  "/ai-providers": "Provedores de IA",
  "/model-routing": "Roteamento",
  "/routing-config": "Configuração de routing",
  "/model-decisions": "Decisões IA",
  "/model-policies": "Políticas de modelo",
  // Sistema
  "/usage": "Uso",
  "/billing": "Billing",
  "/performance": "Performance",
  "/sistema/flags": "Kill switches & flags",
  "/sistema/custo": "Custo & eficiência",
  "/sistema/latencia": "Latência",
  "/sistema/prontidao-comercial": "Prontidão comercial",
  "/sistema/limpeza-ui": "Limpeza de UI",
  "/sistema/glossario": "Glossário",
  "/health": "Saúde",
  "/settings": "Configurações",
  // Administração
  "/admin": "Painel admin",
  "/users": "Usuários",
  "/organizations": "Organizações",
  "/logs": "Logs",
  "/media-logs": "Logs de mídia",
  "/data-management": "Gestão de dados",
  "/blog-admin": "Blog (admin)",
  "/contacts-admin": "Contatos (admin)",
};

function labelFor(pathname: string): string {
  if (routeLabels[pathname]) return routeLabels[pathname];
  // Tenta match por prefixo (ex: /jarvis/session/abc → "Sessão Jarvis")
  if (pathname.startsWith("/jarvis/session/")) {
    return pathname.endsWith("/review") ? "Revisão da sessão" : "Sessão Jarvis";
  }
  if (pathname.startsWith("/blog/")) return "Post do blog";
  // Fallback: title-case do último segmento
  const last = pathname.split("/").filter(Boolean).pop() ?? "";
  return last.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "Página";
}

const pageTransition = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.2, ease: "easeOut" as const },
};

export default function ProtectedLayout({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();
  useRouteTracking(); // MS-09a — telemetria de rotas (auditoria de redirects)

  if (loading)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background text-foreground gap-3">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
        <span className="text-sm text-muted-foreground font-medium">Carregando...</span>
      </div>
    );
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const pageLabel = labelFor(location.pathname);
  const initials = user?.name
    ? user.name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase()
    : "?";

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 flex items-center justify-between px-4 shrink-0 border-b border-border bg-card/80 backdrop-blur-lg sticky top-0 z-30">
            <div className="flex items-center gap-3 min-w-0">
              <SidebarTrigger />
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to="/dashboard" className="text-primary font-semibold text-sm">SYRA</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  {location.pathname !== "/dashboard" && (
                    <>
                      <BreadcrumbSeparator />
                      <BreadcrumbItem>
                        <BreadcrumbPage className="text-sm truncate max-w-[200px]">{pageLabel}</BreadcrumbPage>
                      </BreadcrumbItem>
                    </>
                  )}
                </BreadcrumbList>
              </Breadcrumb>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Avatar className="h-8 w-8 ring-2 ring-primary/10">
                <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                  {initials}
                </AvatarFallback>
              </Avatar>
            </div>
          </header>
          <main className="flex-1 p-4 sm:p-6 overflow-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                {...pageTransition}
              >
                <PaywallGuard>
                  {children}
                </PaywallGuard>
              </motion.div>
            </AnimatePresence>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
